// ═══════════════════════════════════════════════════════════════
// BatchUploadModal.tsx – kötegelt fájlfeltöltés elnevezési konvencióval
//
// - Fájlok, mappák (a mappanévben lévő pontszám is számít) és ZIP-ek (a böngészőben kibontva).
// - A felismert pont a szervezet ÖSSZES oldalán keresendő (pl. az 1. fejezet oldaláról indítva a „2_04_…”
//   fájl a 2. fejezet 2.4-es pontjához kerül). A pontszám szegmensenként, vezető nullák nélkül egyezik
//   („1_08” = „1.8”).
// - Ha a pontnak még nincs bejegyzése: új bejegyzés a 2026-os lista adataival (cím, leírás, megőrzés…),
//   a lista szerinti fejezet oldalán; több fájl ugyanahhoz a ponthoz egy bejegyzést kap.
// - Pont nélküli fájl (pl. „00_Tartalomjegyzék”) alapból kimarad – kézzel hozzárendelhető.
// - A feltöltés nem áll meg hibánál: a már csatolt (azonos nevű) fájlok listába kerülnek, és
//   egyenként vagy együtt lecserélhetők; a többi hiba is tételenként látszik.
// ═══════════════════════════════════════════════════════════════
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ApiClient, type TemplatePointInfo } from '../../api/client';
import { useQuota } from '../../hooks/useQuota';
import type { PageItem, RecordItem, UploadPolicy } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { matchFileToRecords, normalizePoint, mainPoint, type ParsedFileInfo } from '../../utils/fileParser';
import { validateSingleFile, checkStorageQuota, formatBytes } from '../../utils/uploadValidator';
import { readZipFiles, isZipFile } from '../../utils/zipReader';
import {
  Upload, X, FileText, CheckCircle2, AlertTriangle, HelpCircle, Loader2, FolderOpen, HardDrive, RefreshCw, Copy, SkipForward,
} from 'lucide-react';

interface BatchUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  pageId: number;
  pageTitle: string;
  records: RecordItem[];
  onUploadComplete: () => void;
}

type Target = number | 'new' | 'skip';
type Status = 'pending' | 'done' | 'duplicate' | 'replaced' | 'error' | 'skipped';

interface BatchItem extends ParsedFileInfo {
  key: string;
  relPath: string | null;
  target: Target;
  /** új bejegyzés oldala */
  newPageId: number | null;
  tpl: TemplatePointInfo['point'] | null;
  status: Status;
  message?: string;
  dupAttachmentId?: number;
  replace?: boolean;
}

interface Loaded { pages: PageItem[]; records: RecordItem[] }

const parseData = (r: RecordItem): any => r.parsedData || (() => { try { return JSON.parse(r.data || '{}'); } catch { return {}; } })();
const recLabel = (r: RecordItem) => { const d = parseData(r); return `${d.pont ? `${d.pont} ` : ''}${d.cim || d.megnevezes || d.nev || `Bejegyzés #${r.id}`}`; };

/** a mappaútvonal legmélyebb, pontszámmal kezdődő mappája (pl. „1. SZERVEZETI…/1.1 A szerv neve/x.pdf”) */
function pointFromPath(relPath: string | null): string | null {
  if (!relPath) return null;
  const parts = relPath.split('/').slice(0, -1).reverse();
  for (const p of parts) {
    const m = p.match(/^(\d+(?:[._]\d+)+)[\s._-]/);
    if (m) return normalizePoint(m[1]);
  }
  return null;
}

export const BatchUploadModal: React.FC<BatchUploadModalProps> = ({ isOpen, onClose, pageId, pageTitle, records, onUploadComplete }) => {
  const { tenant } = useAuth();
  const [items, setItems] = useState<BatchItem[]>([]);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isReading, setIsReading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; filename: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dirInputRef = useRef<HTMLInputElement>(null);
  const tplCache = useRef(new Map<string, TemplatePointInfo>());

  const [currentStorageBytes, setCurrentStorageBytes] = useState<number>(0);
  const { limitMb: quotaLimitMb, usedBytes: quotaUsedBytes, reload: reloadQuota } = useQuota(isOpen);
  const storageUsedNow = quotaUsedBytes ?? currentStorageBytes;
  const [uploadPolicy, setUploadPolicy] = useState<UploadPolicy | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setFinished(false); setError(null); setLoaded(null);
    Promise.all([ApiClient.getAttachmentsOverview(tenant?.id), ApiClient.getUploadPolicy()]).then(([overviewRes, policyRes]) => {
      const overview: any = (overviewRes as any).data ?? overviewRes;
      if (overviewRes.ok && overview?.total_size !== undefined) setCurrentStorageBytes(Number(overview.total_size) || 0);
      if (policyRes.ok && policyRes.data) setUploadPolicy(policyRes.data);
    }).catch(() => {});
    loadAll();
  }, [isOpen]);

  // a szervezet összes oldala és bejegyzése (a pont bármelyik oldalon lehet) – feltöltés után újra,
  // hogy az újonnan létrehozott bejegyzések a következő körben már célpontként szerepeljenek
  async function loadAll() {
    const pr = await ApiClient.getPages().catch(() => null);
    const pages: PageItem[] = (pr?.ok && pr.data) ? pr.data : [];
    const all: RecordItem[] = [];
    for (const p of pages) {
      const rr = await ApiClient.getPageRecords(p.id).catch(() => null);
      if (rr?.ok && rr.data) all.push(...(rr.data as RecordItem[]).map((r) => ({ ...r, page_id: r.page_id || p.id, parsedData: parseData(r) })));
    }
    setLoaded({
      pages: pages.length ? pages : [{ id: pageId, title: pageTitle } as PageItem],
      records: all.length ? all : records.map((r) => ({ ...r, page_id: r.page_id || pageId })),
    });
  }

  const pageById = useMemo(() => new Map((loaded?.pages || []).map((p) => [p.id, p])), [loaded]);
  const recordsByPage = useMemo(() => {
    const m = new Map<number, RecordItem[]>();
    for (const r of loaded?.records || []) { if (!m.has(r.page_id)) m.set(r.page_id, []); m.get(r.page_id)!.push(r); }
    return m;
  }, [loaded]);

  if (!isOpen) return null;

  /** az új bejegyzés oldala: a 2026-os lista fejezete szerint, különben ahol az azonos fejezetszámú pontok vannak */
  const pageForPoint = (point: string, tpl: TemplatePointInfo | null): number => {
    const pages = loaded?.pages || [];
    if (tpl?.chapter?.slug) { const p = pages.find((x) => x.slug === tpl.chapter!.slug); if (p) return p.id; }
    const chap = point.split('.')[0];
    let best: number | null = null; let bestN = 0;
    for (const [pid, recs] of recordsByPage) {
      const n = recs.filter((r) => mainPoint(parseData(r).pont).split('.')[0] === chap).length;
      if (n > bestN) { best = pid; bestN = n; }
    }
    if (best) return best;
    const byTitle = pages.find((p) => new RegExp(`^\\s*${chap}[.\\s-]`).test(p.title || ''));
    return byTitle?.id || pageId;
  };

  const templateFor = async (point: string): Promise<TemplatePointInfo | null> => {
    if (tplCache.current.has(point)) return tplCache.current.get(point)!;
    const r = await ApiClient.getTemplatePoint(point).catch(() => null);
    const v = r && r.ok ? r : null;
    if (v) tplCache.current.set(point, v);
    return v;
  };

  const addFiles = async (list: { file: File; relPath: string | null }[]) => {
    if (!list.length) return;
    setError(null); setFinished(false);
    setIsReading(true);
    try {
      // ZIP-ek kibontása
      const expanded: { file: File; relPath: string | null }[] = [];
      const errors: string[] = [];
      for (const it of list) {
        if (isZipFile(it.file)) {
          try {
            const entries = await readZipFiles(it.file);
            for (const e of entries) expanded.push({ file: e.file, relPath: e.path });
          } catch (e: any) { errors.push(e.message || `„${it.file.name}”: a ZIP nem olvasható`); }
        } else expanded.push(it);
      }
      const valid: { file: File; relPath: string | null }[] = [];
      for (const it of expanded) {
        const v = validateSingleFile(it.file, uploadPolicy);
        if (v.valid) valid.push(it); else errors.push(v.error || it.file.name);
      }
      const recs = loaded?.records || records;
      const next: BatchItem[] = [];
      for (const it of valid) {
        const parsed = matchFileToRecords(it.file, recs);
        let p: ParsedFileInfo = parsed;
        const fromPath = parsed.detectedPoint ? null : pointFromPath(it.relPath);
        if (fromPath) {
          // a pont a mappanévből: újraillesztés a mappa pontjával
          const fake = new File([it.file], `${fromPath.replace(/\./g, '_')}_${it.file.name}`, { type: it.file.type });
          p = { ...matchFileToRecords(fake, recs), file: it.file, originalName: it.file.name, fileSizeFormatted: parsed.fileSizeFormatted, cleanTitle: parsed.cleanTitle };
        }
        const detected = p.detectedPoint;
        let tpl: TemplatePointInfo | null = null;
        if (!p.matchedRecordId && detected) tpl = await templateFor(detected);
        next.push({
          ...p,
          key: `${it.relPath || it.file.name}|${it.file.size}|${Math.random().toString(36).slice(2, 7)}`,
          relPath: it.relPath,
          target: p.matchedRecordId ? p.matchedRecordId : detected ? 'new' : 'skip',
          newPageId: detected ? pageForPoint(detected, tpl) : pageId,
          tpl: tpl?.found ? tpl.point || null : null,
          status: 'pending',
        });
      }
      setItems((prev) => [...prev.filter((x) => x.status === 'pending'), ...next]);
      if (errors.length) setError(`Kimaradt fájlok:\n${errors.join('\n')}`);
    } finally {
      setIsReading(false);
    }
  };

  const fromFileList = (fl: FileList | null) => (fl ? Array.from(fl).map((f) => ({ file: f, relPath: (f as any).webkitRelativePath || null })) : []);

  const setItem = (key: string, patch: Partial<BatchItem>) => setItems((prev) => prev.map((x) => (x.key === key ? { ...x, ...patch } : x)));

  const handleStartUpload = async () => {
    const todo = items.filter((x) => x.status === 'pending');
    if (!todo.length) return;
    setIsUploading(true); setError(null);
    const attCache = new Map<number, Map<string, number>>(); // bejegyzés → (fájlnév → melléklet-azonosító)
    const created = new Map<string, number>(); // `${oldal}|${pont}` → új bejegyzés
    const existingNames = async (rid: number) => {
      if (!attCache.has(rid)) {
        const r = await ApiClient.getRecordAttachments(rid).catch(() => null);
        attCache.set(rid, new Map(((r?.ok && r.data) ? r.data : []).map((a: any) => [String(a.filename || '').toLowerCase(), a.id])));
      }
      return attCache.get(rid)!;
    };
    let i = 0;
    for (const item of todo) {
      i++;
      setUploadProgress({ current: i, total: todo.length, filename: item.originalName });
      if (item.target === 'skip') { setItem(item.key, { status: 'skipped', message: 'Kihagyva (nincs célpont)' }); continue; }
      try {
        let rid: number;
        if (item.target === 'new') {
          const point = item.detectedPoint || '';
          const pid = item.newPageId || pageId;
          const ck = `${pid}|${point}`;
          if (point && created.has(ck)) rid = created.get(ck)!;
          else {
            const t = item.tpl;
            const payload: Record<string, any> = t
              ? {
                pont: point, cim: t.cim, megnevezes: t.cim,
                ...(t.leiras_html ? { leiras_html: t.leiras_html } : { leiras: t.leiras }),
                kulso_linkek: t.kulso_linkek || '',
                ...(t.frissites ? { frissites: t.frissites } : {}),
                ...(t.megorzesi_ido ? { megorzesi_ido: t.megorzesi_ido } : {}),
                sablon_pont: point,
                ...(t.snippet_ids?.length ? { tartalom_valtozok: t.snippet_ids } : {}),
              }
              : { pont: point || 'Új pont', cim: item.cleanTitle, megnevezes: item.cleanTitle };
            if (item.detectedYear && !t) payload.ev = item.detectedYear;
            const res: any = await ApiClient.saveRecord(pid, payload);
            // a létrehozás válasza { ok, id } (nincs „data” boríték)
            const newId = res?.id ?? res?.data?.id;
            if (!res?.ok || !newId) throw new Error(res?.error || 'A bejegyzés nem hozható létre');
            rid = Number(newId);
            if (point) created.set(ck, rid);
            attCache.set(rid, new Map());
          }
        } else rid = item.target;

        const names = await existingNames(rid);
        const dup = names.get(item.originalName.toLowerCase());
        if (dup) { setItem(item.key, { status: 'duplicate', dupAttachmentId: dup, target: rid, message: 'Már csatolva van ehhez a bejegyzéshez' }); continue; }
        const up: any = await ApiClient.uploadRecordAttachments(rid, [item.file]);
        if (!up?.ok) {
          if (up?.code === 'DUPLICATE' && up?.attachment_id) { setItem(item.key, { status: 'duplicate', dupAttachmentId: up.attachment_id, target: rid, message: 'Már csatolva van ehhez a bejegyzéshez' }); continue; }
          throw new Error(up?.error || 'A feltöltés nem sikerült');
        }
        const newAtt = up.uploaded?.[0]?.id;
        if (newAtt) names.set(item.originalName.toLowerCase(), newAtt);
        setItem(item.key, { status: 'done', target: rid, message: undefined });
      } catch (e: any) {
        setItem(item.key, { status: 'error', message: e?.message || 'Hiba' });
      }
    }
    setIsUploading(false); setUploadProgress(null); setFinished(true);
    reloadQuota();
    await loadAll();
  };

  const replaceSelected = async (onlyKey?: string) => {
    const list = items.filter((x) => x.status === 'duplicate' && x.dupAttachmentId && (onlyKey ? x.key === onlyKey : x.replace));
    if (!list.length) return;
    setIsUploading(true);
    let i = 0;
    for (const it of list) {
      i++;
      setUploadProgress({ current: i, total: list.length, filename: it.originalName });
      const r: any = await ApiClient.replaceAttachment(it.dupAttachmentId!, it.file).catch((e: any) => ({ ok: false, error: e?.message }));
      setItem(it.key, r?.ok ? { status: 'replaced', message: r.archived ? 'Lecserélve – a korábbi fájl az Archívumba került' : 'Lecserélve' } : { message: r?.error || 'A csere nem sikerült' });
    }
    setIsUploading(false); setUploadProgress(null); setFinished(true);
    reloadQuota();
  };

  const close = () => {
    if (finished) onUploadComplete();
    setItems([]); setFinished(false); setError(null);
    onClose();
  };

  const pending = items.filter((x) => x.status === 'pending');
  const toUpload = pending.filter((x) => x.target !== 'skip');
  const counts = items.reduce((m, x) => { m[x.status] = (m[x.status] || 0) + 1; return m; }, {} as Partial<Record<Status, number>>);
  const dups = items.filter((x) => x.status === 'duplicate');
  const totalBatchBytes = toUpload.reduce((sum, item) => sum + (item.file.size || 0), 0);
  const maxStorageMb = quotaLimitMb ?? tenant?.max_storage_mb ?? 500;
  const quota = checkStorageQuota(storageUsedNow, totalBatchBytes, maxStorageMb);
  const pageName = (pid: number | null) => (pid && pageById.get(pid)?.title) || pageTitle;

  const statusBadge = (x: BatchItem) => {
    if (x.status === 'pending') return null;
    const cls: Record<Exclude<Status, 'pending'>, string> = {
      done: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300',
      replaced: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300',
      duplicate: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300',
      error: 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300',
      skipped: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
    };
    const label: Record<Exclude<Status, 'pending'>, string> = { done: 'Feltöltve', replaced: 'Lecserélve', duplicate: 'Már csatolva', error: 'Hiba', skipped: 'Kihagyva' };
    return <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${cls[x.status]}`}>{label[x.status]}</span>;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in" role="dialog" aria-modal="true" aria-labelledby="batch-title">
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between bg-neutral-50 dark:bg-neutral-800/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center"><FolderOpen className="w-5 h-5" /></div>
            <div>
              <h2 id="batch-title" className="text-lg font-bold text-neutral-900 dark:text-white">Kötegelt fájlfeltöltés elnevezési konvencióval</h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Indítva innen: <span className="font-semibold text-neutral-700 dark:text-neutral-200">{pageTitle}</span> — a pontok a szervezet összes oldalán keresve
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowHelp(!showHelp)} aria-label="Súgó" className="p-2 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-700 transition" title="Elnevezési minták és súgó"><HelpCircle className="w-5 h-5" /></button>
            <button onClick={close} disabled={isUploading} aria-label="Bezárás" className="p-2 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {showHelp && (
          <div className="px-6 py-3 bg-blue-50/80 dark:bg-blue-950/40 border-b border-blue-100 dark:border-blue-900/30 text-xs text-blue-900 dark:text-blue-200 space-y-1">
            <div className="font-semibold text-blue-800 dark:text-blue-300">💡 Fájlnév-konvenciók, amiket a rendszer felismer:</div>
            <ul className="list-disc pl-5 space-y-0.5 text-neutral-600 dark:text-neutral-300">
              <li><code className="font-mono">1_08_Alapitott_kozalapitvanyok.docx</code> ➔ <b>1.8. pont</b> (a vezető nulla nem számít)</li>
              <li><code className="font-mono">3.2.1_Éves_költségvetés_2026.pdf</code> ➔ <b>3.2.1. pont</b> (év: 2026)</li>
              <li><code className="font-mono">1_pont_szervezeti_felépítés.pdf</code>, <code className="font-mono">pont_4_….docx</code> ➔ <b>1.</b> / <b>4. pont</b></li>
              <li>Mappából vagy ZIP-ből a mappanév pontja is számít: <code className="font-mono">…/1.1 A szerv neve/alapito_okirat.pdf</code> ➔ <b>1.1</b></li>
              <li>A pont a szervezet bármelyik oldalán lehet; ha még nincs bejegyzése, a 2026-os lista adataival jön létre a megfelelő fejezet oldalán.</li>
              <li>Pont nélküli fájl (pl. tartalomjegyzék) alapból kimarad; a célpont minden sorban átállítható.</li>
            </ul>
          </div>
        )}

        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          <div
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); addFiles(fromFileList(e.dataTransfer.files)); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            className="border-2 border-dashed border-neutral-300 dark:border-neutral-700 hover:border-blue-500 bg-neutral-50/50 dark:bg-neutral-800/30 rounded-2xl p-5 text-center flex flex-col items-center justify-center gap-3"
          >
            <input type="file" ref={fileInputRef} onChange={(e) => { addFiles(fromFileList(e.target.files)); e.target.value = ''; }} multiple className="hidden" />
            <input type="file" ref={dirInputRef} onChange={(e) => { addFiles(fromFileList(e.target.files)); e.target.value = ''; }} multiple className="hidden"
              {...({ webkitdirectory: '', directory: '' } as any)} />
            <div className="w-12 h-12 rounded-2xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center shadow-sm">
              {isReading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Upload className="w-6 h-6" />}
            </div>
            <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">Húzza ide a fájlokat vagy a ZIP-et, vagy válasszon:</p>
            <div className="flex flex-wrap justify-center gap-2">
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading || isReading} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold cursor-pointer disabled:opacity-50">Fájlok vagy ZIP…</button>
              <button type="button" onClick={() => dirInputRef.current?.click()} disabled={isUploading || isReading} className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 text-xs font-semibold cursor-pointer disabled:opacity-50">Teljes mappa…</button>
            </div>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">PDF, DOCX, XLSX, XLS, CSV, JPG, PNG – a ZIP a böngészőben bomlik ki{!loaded ? ' · az oldalak betöltése…' : ''}</p>
          </div>

          {error && (
            <div role="alert" className="p-3 rounded-xl bg-red-50 dark:bg-red-950/50 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs flex items-start gap-2 whitespace-pre-line">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{error}</span>
            </div>
          )}

          {isUploading && uploadProgress && (
            <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 space-y-2" role="status">
              <div className="flex items-center justify-between text-xs font-medium text-blue-900 dark:text-blue-200">
                <span className="flex items-center gap-2 min-w-0"><Loader2 className="w-4 h-4 animate-spin" /> <b className="truncate">{uploadProgress.filename}</b></span>
                <span>{uploadProgress.current} / {uploadProgress.total}</span>
              </div>
              <div className="w-full bg-blue-200 dark:bg-blue-900/50 h-2 rounded-full overflow-hidden">
                <div className="bg-blue-600 h-full rounded-full transition-all duration-300" style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }} />
              </div>
            </div>
          )}

          {finished && (
            <div role="status" className="p-3 rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40 text-xs text-emerald-900 dark:text-emerald-200 space-y-2">
              <div className="flex items-center gap-2 font-semibold"><CheckCircle2 className="w-4 h-4" />
                Kész: {counts.done || 0} feltöltve{counts.replaced ? `, ${counts.replaced} lecserélve` : ''}{counts.duplicate ? `, ${counts.duplicate} már csatolva` : ''}{counts.skipped ? `, ${counts.skipped} kihagyva` : ''}{counts.error ? `, ${counts.error} hiba` : ''}.
              </div>
              {dups.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 text-amber-900 dark:text-amber-200">
                  <span>A már csatolt fájlok lecserélhetők (bejelölve vagy soronként):</span>
                  <button type="button" onClick={() => setItems((prev) => prev.map((x) => (x.status === 'duplicate' ? { ...x, replace: true } : x)))} className="underline cursor-pointer">mind kijelölése</button>
                  <button type="button" onClick={() => replaceSelected()} disabled={isUploading || !dups.some((x) => x.replace)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-600 hover:bg-amber-500 text-white font-semibold cursor-pointer disabled:opacity-50">
                    <RefreshCw className="w-3.5 h-3.5" /> Kijelöltek cseréje ({dups.filter((x) => x.replace).length})
                  </button>
                </div>
              )}
            </div>
          )}

          {items.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-neutral-800 dark:text-neutral-200">Fájlok ({items.length} db{pending.length !== items.length ? `, ${pending.length} vár feltöltésre` : ''})</h3>
                {!isUploading && <button onClick={() => { setItems([]); setFinished(false); }} className="text-xs text-red-600 dark:text-red-400 hover:underline">Lista ürítése</button>}
              </div>
              <div className="border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden divide-y divide-neutral-200 dark:divide-neutral-800">
                {items.map((item) => {
                  const locked = item.status !== 'pending';
                  const newLabel = item.detectedPoint
                    ? `＋ Új bejegyzés: ${item.detectedPoint}${item.tpl ? ` – ${(item.tpl.short || item.tpl.cim).slice(0, 40)}` : ` (${item.cleanTitle.slice(0, 30)})`} → ${pageName(item.newPageId).slice(0, 30)}`
                    : '＋ Új bejegyzés ezen az oldalon';
                  return (
                    <div key={item.key} className="p-3 bg-white dark:bg-neutral-900 flex flex-col md:flex-row md:items-center justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 flex items-center justify-center flex-shrink-0 mt-0.5"><FileText className="w-4 h-4" /></div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-xs text-neutral-900 dark:text-white break-all">{item.originalName}</span>
                            <span className="text-[10px] text-neutral-400 font-mono">({item.fileSizeFormatted})</span>
                            {item.detectedPoint && <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">Pont: {item.detectedPoint}</span>}
                            {item.detectedYear && <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">Év: {item.detectedYear}</span>}
                            {statusBadge(item)}
                          </div>
                          <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
                            {item.relPath && item.relPath !== item.originalName ? <>Útvonal: <span className="font-mono">{item.relPath}</span> · </> : null}
                            {item.message ? <span className={item.status === 'error' ? 'text-rose-600 dark:text-rose-400' : ''}>{item.message}</span> : <>Megnevezés: <span className="italic">{item.cleanTitle}</span></>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {item.status === 'duplicate' ? (
                          <>
                            <label className="inline-flex items-center gap-1.5 text-xs text-amber-900 dark:text-amber-200 cursor-pointer">
                              <input type="checkbox" checked={Boolean(item.replace)} onChange={(e) => setItem(item.key, { replace: e.target.checked })} /> Csere
                            </label>
                            <button type="button" onClick={() => replaceSelected(item.key)} disabled={isUploading} title="A már csatolt fájl lecserélése erre"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs border border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-300 cursor-pointer disabled:opacity-50">
                              <Copy className="w-3.5 h-3.5" /> Csere most
                            </button>
                          </>
                        ) : (
                          <div className="w-full md:w-80">
                            <select
                              value={String(item.target)}
                              onChange={(e) => setItem(item.key, { target: e.target.value === 'new' || e.target.value === 'skip' ? e.target.value : Number(e.target.value) })}
                              disabled={isUploading || locked}
                              aria-label={`${item.originalName} célpontja`}
                              className="w-full text-xs px-2.5 py-1.5 bg-neutral-50 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg text-neutral-900 dark:text-white disabled:opacity-60"
                            >
                              <option value="skip">— Kihagyás —</option>
                              <option value="new">{newLabel}</option>
                              {(loaded?.pages || []).map((pg) => {
                                const recs = recordsByPage.get(pg.id) || [];
                                if (!recs.length) return null;
                                return (
                                  <optgroup key={pg.id} label={pg.title}>
                                    {recs.map((r) => <option key={r.id} value={r.id}>{recLabel(r).slice(0, 70)}</option>)}
                                  </optgroup>
                                );
                              })}
                            </select>
                          </div>
                        )}
                        {item.status === 'pending' && !isUploading && (
                          <button onClick={() => setItems((prev) => prev.filter((x) => x.key !== item.key))} className="p-1.5 text-neutral-400 hover:text-red-600 rounded-lg transition" title="Eltávolítás a listából" aria-label="Eltávolítás"><X className="w-4 h-4" /></button>
                        )}
                        {item.status === 'skipped' && <SkipForward className="w-4 h-4 text-neutral-400" aria-hidden />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-neutral-50 dark:bg-neutral-800/50">
          <div className="text-xs text-neutral-500 dark:text-neutral-400 space-y-1">
            {pending.length > 0 ? (
              <div>
                <span><b>{toUpload.length}</b> fájl ({formatBytes(totalBatchBytes)}) feltöltésre kész{pending.length - toUpload.length ? `, ${pending.length - toUpload.length} kihagyva` : ''}</span>
                <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-slate-500"><HardDrive className="w-3.5 h-3.5" />
                  <span>Kvóta: {formatBytes(storageUsedNow + totalBatchBytes)} / {maxStorageMb > 0 ? `${maxStorageMb} MB` : 'korlátlan'} ({quota.usedPercent}%)</span>
                </div>
              </div>
            ) : <span>{items.length ? 'Nincs feltöltésre váró fájl.' : 'Válasszon ki vagy húzzon be fájlokat a kezdéshez.'}</span>}
            {quota.isOverQuota && <p className="text-red-600 dark:text-red-400 font-bold">⚠️ Kvótatúllépés! A fájlokkal a szervezet elérné a tárhelykorlátot.</p>}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={close} disabled={isUploading} className="px-4 py-2 text-xs font-semibold text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-xl transition">{finished ? 'Kész' : 'Mégse'}</button>
            <button
              onClick={handleStartUpload}
              disabled={!toUpload.length || isUploading || isReading || !loaded || quota.isOverQuota}
              className="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl shadow-md transition flex items-center gap-2"
            >
              {isUploading ? <><Loader2 className="w-4 h-4 animate-spin" /><span>Feltöltés folyamatban…</span></> : <><Upload className="w-4 h-4" /><span>{toUpload.length} fájl feltöltése és hozzárendelése</span></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
